import {
  mdiAccountCircleOutline,
  mdiAccountGroupOutline,
  mdiChevronRight,
  mdiCreditCardOutline,
  mdiFileDocumentOutline,
  mdiHelpCircleOutline,
  mdiLogoutVariant,
  mdiOfficeBuildingOutline,
  mdiSwapHorizontal
} from '@mdi/js';
import Icon from '@mdi/react';
import { Box, ButtonBase, Typography } from '@mui/material';
import { NavLink, useLocation } from 'react-router-dom';

import { Routes } from '../../../routes/index.routes';
import { Colors } from '../../../shared/colors';

interface ResponsiveMenuProps {
  onNavigate: VoidFunction;
  usuarioPJ: boolean;
  nome?: string;
  onAjuda?: VoidFunction;
  onSair: VoidFunction;
}

interface ResponsiveMenuActionProps {
  icon: string;
  label: string;
  onClick: VoidFunction;
}

interface ResponsiveMenuItemProps {
  active: boolean;
  icon: string;
  label: string;
  onClick: VoidFunction;
  to: string;
}

const divider = '#D8E0EA';
const textColor = '#102A56';

const ResponsiveMenuItem = ({ active, icon, label, onClick, to }: ResponsiveMenuItemProps) => (
  <ButtonBase
    component={NavLink}
    to={to}
    onClick={onClick}
    sx={{
      alignItems: 'center',
      bgcolor: active ? Colors.primary.light : Colors.white,
      borderBottom: `1px solid ${divider}`,
      color: active ? Colors.primary.main : textColor,
      display: 'flex',
      gap: 2,
      justifyContent: 'flex-start',
      minHeight: 64,
      px: 3,
      textAlign: 'left',
      textDecoration: 'none',
      width: '100%',
      '&:hover': { bgcolor: Colors.primary.light }
    }}
  >
    <Box display="flex" color={Colors.primary.main} width={26} flexShrink={0}>
      <Icon path={icon} size={1.1} />
    </Box>
    <Typography fontSize={16} fontWeight={active ? 700 : 500}>
      {label}
    </Typography>
  </ButtonBase>
);

const ResponsiveMenuAction = ({ icon, label, onClick }: ResponsiveMenuActionProps) => (
  <ButtonBase
    onClick={onClick}
    sx={{
      alignItems: 'center',
      color: textColor,
      display: 'flex',
      gap: 2,
      justifyContent: 'flex-start',
      minHeight: 56,
      px: 3,
      textAlign: 'left',
      width: '100%',
      '&:hover': { bgcolor: Colors.primary.light }
    }}
  >
    <Box display="flex" color={Colors.primary.main} width={26} flexShrink={0}>
      <Icon path={icon} size={1.1} />
    </Box>
    <Typography fontSize={16} fontWeight={500}>
      {label}
    </Typography>
  </ButtonBase>
);

export function ResponsiveMenu({
  onNavigate,
  usuarioPJ,
  nome,
  onAjuda,
  onSair
}: ResponsiveMenuProps) {
  const location = useLocation();
  const isActive = (to: string) =>
    to === Routes.Home ? location.pathname === to : location.pathname.startsWith(to);

  return (
    <Box display="flex" flexDirection="column" minHeight="100%">
      <ButtonBase
        component={NavLink}
        to={Routes.Conta.Principal}
        onClick={onNavigate}
        sx={{
          alignItems: 'center',
          bgcolor: Colors.white,
          borderBottom: `1px solid ${divider}`,
          color: textColor,
          display: 'flex',
          gap: 2,
          justifyContent: 'flex-start',
          px: 3,
          py: 3,
          textAlign: 'left',
          textDecoration: 'none',
          width: '100%'
        }}
      >
        <Box display="flex" color={Colors.primary.dark} flexShrink={0}>
          <Icon path={usuarioPJ ? mdiOfficeBuildingOutline : mdiAccountCircleOutline} size={1.4} />
        </Box>
        <Box display="flex" flexDirection="column" flex={1} minWidth={0}>
          <Typography fontSize={16} fontWeight={700} noWrap>
            {nome}
          </Typography>
          <Typography fontSize={13} color={Colors.text.label}>
            {usuarioPJ ? 'Pessoa jurídica' : 'Pessoa física'} · Ver dados
          </Typography>
        </Box>
        <Icon path={mdiChevronRight} size={0.9} />
      </ButtonBase>

      <ResponsiveMenuItem
        to={Routes.Home}
        icon={mdiCreditCardOutline}
        label="Carteira"
        active={isActive(Routes.Home)}
        onClick={onNavigate}
      />
      {usuarioPJ && (
        <>
          <ResponsiveMenuItem
            to={Routes.Usuarios.Principal}
            icon={mdiAccountGroupOutline}
            label="Gestão de usuários"
            active={isActive(Routes.Usuarios.Principal)}
            onClick={onNavigate}
          />
          <ResponsiveMenuItem
            to={Routes.Transacoes.Principal}
            icon={mdiSwapHorizontal}
            label="Transações de usuários"
            active={isActive(Routes.Transacoes.Principal)}
            onClick={onNavigate}
          />
          <ResponsiveMenuItem
            to={Routes.Extrato.Principal}
            icon={mdiFileDocumentOutline}
            label="Consumo pós-pago"
            active={isActive(Routes.Extrato.Principal)}
            onClick={onNavigate}
          />
        </>
      )}

      <Box mt="auto" pt={2} pb={1} borderTop={`1px solid ${divider}`}>
        {onAjuda && (
          <ResponsiveMenuAction icon={mdiHelpCircleOutline} label="Ajuda" onClick={onAjuda} />
        )}
        <ResponsiveMenuAction icon={mdiLogoutVariant} label="Sair" onClick={onSair} />
      </Box>
    </Box>
  );
}
